import React, { useContext, useEffect } from 'react';
import { Container, Row, Col, Badge, Table } from 'react-bootstrap';
import { AppContext } from '../context/AppContext';
import {  isUpcoming, sortedEventsByExpireDate } from '../services/date';
import { EventItem } from '../models/models';
import { SCNavbar } from '../components/Navbar';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';
import DOMPurify from 'dompurify';


const EventsPage: React.FC = () => {

  const context = useContext(AppContext);
  if (!context) {
    throw new Error("AppContext must be used within an AppProvider");
  }

  const { state, updateScientificCommittee } = context;
  const { scientificCommittee } = useParams<{ scientificCommittee: string; type: string; id: string }>();

  const [ hideFutureEvents, setHideFutureEvents ] = React.useState(true);  
  
  const data = state?.data;
  const title = data?.scientific_committee?.name;

  useEffect(() => {
    if (scientificCommittee) {
      updateScientificCommittee(scientificCommittee);
    }
  }, [scientificCommittee, updateScientificCommittee]);


  //outuput example: December 11, 2025
  const convertEventDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!data || !data.events) {
    return (
      <Container className="py-5">
        <Row>
          <Col>
            <h1>Past events</h1>
            <p>No events available.</p>
          </Col>
        </Row>
      </Container>
    );
  }

  const sortedEvents = sortedEventsByExpireDate(data.events);
  const filteredEvents = hideFutureEvents ? sortedEvents.filter(event => !isUpcoming(event.expireDate)) : sortedEvents;

  const scrollToEvent = (eventId: number) => {
    const element = document.getElementById(`event-${eventId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
    <Helmet>
        <title>{title} past events | ICOH - International Commission on Occupational Health</title>
        <meta name="description" content="Events page for ICOH - International Commission on Occupational Health" />
        <link rel="canonical" href={`${process.env.REACT_APP_BASE_PATH}/${scientificCommittee}/events`} />
    </Helmet>
    <SCNavbar scientificCommittee={scientificCommittee} section="events" />
      
    <Container className="py-5">
      <Row>
        <Col>

          <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="mb-4">{hideFutureEvents ? "Past events" : "All events"}</h1>
          <button 
            className="btn btn-outline-primary"
            onClick={() => setHideFutureEvents(!hideFutureEvents)}
          >
            {hideFutureEvents ? "Show all events" : "Show past events"}
          </button>

          </div>
            <Table striped hover className="align-middle">
              <tbody>
               
                {filteredEvents.map((event: EventItem) => (
                  <tr 
                    key={event.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => scrollToEvent(event.id)}
                  >
                    <td style={{ fontWeight: '600' }}>
                      {isUpcoming(event.expireDate) && (
                          <Badge bg="primary" className="ms-2">
                            Upcoming event
                          </Badge>
                        )}
                    </td>
                    <td>
                      {(event.period && <strong>{event.period}</strong>) || (event.date && <em>{convertEventDate(event.date)}</em>)}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        {event.title}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
        </Col>
      </Row>
    </Container>
    <section className='bg-quaternary py-4'>
        <Container> 
            <Row>
                <Col>
                    {filteredEvents.map((event: EventItem, index: number) => (
                        <div key={event.id} id={`event-${event.id}`} className="mb-5">
                            {index > 0 && <hr className="my-4" />}
                            
                            <div className="mb-3">
                            
                            <h3 className="text-primary mb-2">
                                {event.title}
                            </h3>
                            <h4 className="mb-3">{event.period} {event.period && event.place ? '-' : ''} {event.place}</h4>
                            </div>
                            <div 
                            className="text-muted"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(event.text) }}
                            />
                        </div>
                    ))}
                </Col>
            </Row>
        </Container>
    </section>
    </>
  );
};

export default EventsPage;